import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import {
  useListTemplates,
  useGetTemplate,
  useGetSenderAddress,
  useGetBrandSettings,
  useRenderTemplate,
  useSendEmail,
  useAiGenerateTemplate,
  useCreateTemplate,
  useGetDashboardSummary,
  useHealthCheck,
  useListContacts,
  useGetRepDefaults,
  useGetMe,
  useGetGmailStatus,
  useUpdateMePreferences,
  getTemplateAutoDefaults,
  getHealthCheckQueryKey,
  getListEmailLogsQueryKey,
  getGetDashboardSummaryQueryKey,
  getGetMeQueryKey,
  getGetTemplateQueryKey,
  getListTemplatesQueryKey,
  getPeekNextCaseReferenceQueryKey,
  type SendEmailInput,
  type AiGenerateInput,
  type AiGenerateInputLayoutHint,
  type AiGenerateInputTone,
  type CreateTemplateInput,
  type CreateTemplateInputLayout,
  type DashboardSummary,
  type Contact,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AI_LAYOUT_HINT_VALUES, LAYOUT_LABELS, AI_TONE_VALUES, AI_TONE_LABELS } from "@/lib/enumValues";
import { useDebounce } from "@/hooks/use-debounce";
import { reasonLabel as verdictReasonLabel } from "@/components/EmailVerdictBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Send, Mail, Sparkles, Pencil, RotateCcw, AlertTriangle, Users, Search, Reply, Info, BookmarkPlus, FlaskConical, Check } from "lucide-react";
import { capitalizeName } from "@/lib/capitalizeName";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  apiErrorCode,
  apiErrorData,
  apiErrorMessage,
  apiErrorRateLimit,
  apiErrorStatus,
} from "@/lib/apiError";
import { COMPOSE_PREFILL_KEY, type ComposePrefill } from "@/lib/composePrefill";
import { isAutoSuffixedSlug } from "@/lib/templateSlug";
import { rewriteLinksForPreview } from "@/lib/previewHtml";
import { MultiClientPreview } from "@/components/multi-client-preview";
import {
  RecheckSenderButton,
  type RecheckCooldownState,
} from "@/components/RecheckSenderButton";
import { SenderStatusIndicator } from "@/components/SenderStatusIndicator";
import {
  SubjectsButton,
  ToneButton,
  SpamCheckButton,
} from "@/components/ai-writing-toolbar";
import { AiRateLimitInline } from "@/components/AiRateLimitInline";
import { AttachmentsField } from "@/components/attachments-field";
import type { ComposeAttachment } from "@/lib/attachments";
import {
  notifyFailure as notifySendFailure,
  watchPendingSend,
} from "@/lib/pendingSendNotifier";

const FOLLOWUP_STORAGE_KEY = "mailcraft:compose-followup";

interface FollowUpHandoff {
  to: string;
  subject: string;
  bodyHtml: string;
}

function consumeFollowUpHandoff(): FollowUpHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(FOLLOWUP_STORAGE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(FOLLOWUP_STORAGE_KEY);
    const parsed = JSON.parse(raw) as Partial<FollowUpHandoff>;
    if (
      typeof parsed.to === "string" &&
      typeof parsed.subject === "string" &&
      typeof parsed.bodyHtml === "string"
    ) {
      return {
        to: parsed.to,
        subject: parsed.subject,
        bodyHtml: parsed.bodyHtml,
      };
    }
  } catch {
    /* fall through */
  }
  return null;
}

type SubjectMode = "auto" | "manual";

// The Post-Call Thank You template is the default starting point in
// Compose. It is identified by the stable seed slug from the API server's
// builtin templates list — never by id, since the id depends on insert
// order and varies between environments.
const DEFAULT_TEMPLATE_SLUG = "post-call-thank-you";

// Defaults pre-filled into the compose form on a fresh open. These match
// the server-side fallbacks (`MAILCRAFT_FROM_EMAIL` and the "Support Team"
// effective From-name) so the visible field values mirror what the server
// would have used anyway when the override was left blank — power users
// can clear or change them per-send.
const DEFAULT_REPLY_TO = "support@cloudaiassistance.com";
const DEFAULT_FROM_NAME = "Support Team";

// Today's date in `YYYY-MM-DD` form. Matches the `<input type="date">`
// value format and the `date` template-variable shape so the field is
// pre-populated with today on every fresh compose.
function todayDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// `caseReference` defaults are now resolved entirely server-side via
// `GET /templates/:id/auto-defaults` — for templates with their own
// `autoGenerate` descriptor (PayPal Fraud) the server uses its dedicated
// counter; for everything else the server returns a value derived from
// the rep's saved Case ID, incremented per fresh compose. The previous
// client-side rotating-digit cursor (`PYPL-1108301700..9` cycle in
// localStorage) is gone — see task #256.

// Lightweight RFC-5322-ish check that matches what the API's zod `format: email`
// rejects in practice (typos, missing `@`, missing TLD, internal whitespace).
// Kept intentionally simple — the server is still the source of truth.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Lightweight URL check that catches the common typos people make in
// `type: "url"` template variables — missing/misspelled scheme
// ("htps://...", "example.com"), spaces, etc. Matches what the browser's
// native URL parser would accept and requires an http(s) scheme so a
// typo'd link doesn't bake an unclickable string into the email body.
function isValidUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

// Each item is validated independently so users can paste comma-separated lists.
function findInvalidRecipients(value: string): string[] {
  if (!value.trim()) return [];
  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .filter((part) => !EMAIL_REGEX.test(part));
}

function splitRecipients(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function recipientErrorText(field: string, invalid: string[]): string | null {
  if (invalid.length === 0) return null;
  const quoted = invalid.map((i) => `"${i}"`).join(", ");
  return invalid.length === 1
    ? `${field} contains an invalid email: ${quoted}`
    : `${field} contains invalid emails: ${quoted}`;
}

export default function Compose() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(window.location.search);
  const initialTemplateId = searchParams.get("templateId");
  const isFollowUp = searchParams.get("followUp") === "1";
  const [followUpHandoff] = useState<FollowUpHandoff | null>(() =>
    isFollowUp ? consumeFollowUpHandoff() : null,
  );
  // When the inbox routes us here as a reply (manual or AI), it
  // passes pre-fill query params. Reading these once at mount keeps
  // the rest of the form state behavior unchanged.
  const replyTo = searchParams.get("to") ?? "";
  const replySubject = searchParams.get("subject") ?? "";
  const replyBody = searchParams.get("body") ?? "";
  const replyFromMailbox = searchParams.get("from-mailbox") ?? "";
  // The inbox deep-link also carries the inbound mailbox as a
  // `reply-to` URL param so the Compose Reply-To input gets pre-filled
  // with the same address the inline-reply panel uses on /send. Without
  // this, picking "Reply in Compose" silently routes replies through
  // the verified shared sender instead of the per-mailbox inbox.
  const replyToParam = searchParams.get("reply-to") ?? "";
  // When the reply pre-fill came from the inbox, this carries the
  // inbound id so /send can thread the outgoing message back to the
  // same conversation (sets In-Reply-To + References + persists
  // thread_id on the email log).
  const replyInboundEmailIdRaw = searchParams.get("inboundEmailId");
  const replyInboundEmailId = (() => {
    if (!replyInboundEmailIdRaw) return null;
    const n = parseInt(replyInboundEmailIdRaw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const isReplyPrefill = replyTo !== "" || replySubject !== "" || replyBody !== "";

  const { data: sender, isLoading: senderLoading } = useGetSenderAddress();
  const { data: brandSettings } = useGetBrandSettings();
  const { data: templates, isLoading: templatesLoading } = useListTemplates({
    source: "all",
  });
  useGetDashboardSummary();
  const healthQuery = useHealthCheck({
    query: {
      queryKey: getHealthCheckQueryKey(),
      staleTime: 5 * 60 * 1000,
    },
  });
  const senderConfig = healthQuery.data?.senderConfig;
  const isSenderUnverified = senderConfig?.status === "unverified";
  const [senderRecheckCooldown, setSenderRecheckCooldown] =
    useState<RecheckCooldownState | null>(null);

  // One-shot pre-fill from "Use as new email" on the log detail page.
  // Read & clear it BEFORE useState so we can seed initial state directly,
  // avoiding a fight with the templateId-change reset effect below.
  const initialPrefill = useMemo<ComposePrefill | null>(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(COMPOSE_PREFILL_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      sessionStorage.removeItem(COMPOSE_PREFILL_KEY);
    } catch {}
    try {
      const parsed = JSON.parse(raw) as ComposePrefill;
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  }, []);

  // Whichever pre-fill source is present wins. All three routes set templateId="blank".
  // Priority on conflict: URL search-param reply > sessionStorage follow-up > sessionStorage "Use as new email".
  const hasInitialPrefill = !!(initialPrefill || followUpHandoff || isReplyPrefill);

  const [templateId, setTemplateId] = useState<string>(
    hasInitialPrefill ? "blank" : initialTemplateId || "",
  );

  // For a fresh new compose (no prefill, no explicit ?templateId=), stamp
  // today's date onto the post-call template's `callDate` variable when
  // that template is auto-selected. The matching `caseReference` value is
  // resolved server-side via the auto-defaults endpoint (rep's saved Case
  // ID, incremented per fresh compose) so the only thing we need to seed
  // eagerly here is the date. We pin it once on mount so the displayed
  // value is stable even if the effect happens to straddle midnight.
  const isFreshNewCompose = !hasInitialPrefill && !initialTemplateId;
  const defaultTemplateAutoFillsRef = useRef<{
    callDate: string;
  } | null>(null);
  if (isFreshNewCompose && defaultTemplateAutoFillsRef.current === null) {
    defaultTemplateAutoFillsRef.current = {
      callDate: todayDateString(),
    };
  }

  // Tracks which template id we've already issued server-side
  // auto-defaults for (e.g. PYPL-FRD-… for the PayPal fraud template's
  // dedicated counter, or the rep-driven `caseReference` cursor on every
  // other template). Without this, the auto-default fetch effect could
  // re-run when its OTHER deps change (e.g. `repDefaults` resolving)
  // and consume an extra cursor number, drifting the displayed value
  // from the issued value. We bump the counter at most once per
  // template selection per mount.
  const autoDefaultsIssuedForTemplateIdRef = useRef<number | null>(null);
  const [autoFilledCaseRef, setAutoFilledCaseRef] = useState<string | null>(null);

  const [to, setTo] = useState(
    isReplyPrefill
      ? replyTo
      : (initialPrefill?.to ?? followUpHandoff?.to ?? ""),
  );

  const [cc, setCc] = useState(initialPrefill?.cc ?? "");
  const [bcc, setBcc] = useState(initialPrefill?.bcc ?? "");

  // Optional Reply-To override. The field is pre-filled (in a one-shot
  // effect below) with the user's saved Brand support email when set, or
  // the shared "support@cloudaiassistance.com" fallback when not — so a
  // fresh compose always shows a sensible default in the field rather than
  // an empty input, but users with brand-level support inboxes still see
  // their own. Reply-prefill flows (clicking "Reply" from the inbox) take
  // precedence and seed the original sender directly. Users can clear or
  // change the field to redirect replies elsewhere. The single-address
  // shape mirrors the `replyToOverride` field on `POST /send` (OpenAPI
  // `format: email`).
  const [replyToOverride, setReplyToOverride] = useState(
    isReplyPrefill ? replyToParam : "",
  );
  // True once the user has typed in the Reply-To input, so the brand-load
  // effect doesn't clobber their edit.
  const replyToTouchedRef = useRef(false);

  // Per-send From overrides. The "From name" is pre-filled (one-shot effect
  // below) with the user's saved Brand From-name when set, or the standard
  // "Support Team" friendly-name fallback when not — matching the server's
  // `effectiveFromName` resolution order. The local-part override stays
  // empty so it falls through to the verified shared sender unless the
  // user explicitly types something.
  const [fromNameOverride, setFromNameOverride] = useState("");
  const fromNameTouchedRef = useRef(false);
  const [fromLocalPartOverride, setFromLocalPartOverride] = useState("");
  // Mirror of the touched-ref pattern above so a user's saved
  // `defaultFromLocalPart` only auto-fills the field once. After they
  // type (or clear) it, we never touch it again — they may want to send
  // from the brand default for this one email without losing their
  // saved alias preference.
  const fromLocalPartTouchedRef = useRef(false);

  const [subjectMode, setSubjectMode] = useState<SubjectMode>("auto");
  const [manualSubject, setManualSubject] = useState("");
  const [blankSubject, setBlankSubject] = useState(
    isReplyPrefill
      ? replySubject
      : (initialPrefill?.subject ?? followUpHandoff?.subject ?? ""),
  );

  const [variables, setVariables] = useState<Record<string, string>>({});

  const [contactPickerOpen, setContactPickerOpen] = useState(false);

  // File attachments — UI + base64 conversion lives in the shared
  // <AttachmentsField/>. Compose still owns the list state because it
  // needs to be reset on template change, included in the /send payload,
  // and inspected when computing the send button's disabled state.
  const [attachments, setAttachments] = useState<ComposeAttachment[]>([]);
  const [contactSearch, setContactSearch] = useState("");
  const { data: contacts } = useListContacts();
  const { data: repDefaults } = useGetRepDefaults();
  // Pull /me so we can apply per-user "From" defaults (defaultFromName +
  // defaultFromLocalPart) ahead of brand-wide defaults, and so we know
  // whether the signed-in user is admin (used to gate adminOnly template
  // variables in the inputs panel below).
  const { data: meUser, isFetched: meFetched } = useGetMe();
  // Optional: per-user Gmail connection. When the user has linked their
  // @update2factor.com Google inbox we pre-fill Reply-To with that
  // address so customer replies go straight back into their personal
  // Gmail. Otherwise we fall back to the Clerk /me email (which for
  // Google sign-in is also their @update2factor.com address) and finally
  // the brand's shared support inbox.
  const { data: gmailStatus } = useGetGmailStatus();

  // Pre-fill the body. Priority: reply (escaped + <pre>) > follow-up handoff > session prefill.
  const initialBodyHtml = (() => {
    if (isReplyPrefill) {
      if (!replyBody) return "";
      const escaped = replyBody
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return `<pre style="font-family: -apple-system, system-ui, sans-serif; white-space: pre-wrap; padding: 16px; line-height: 1.5;">${escaped}</pre>`;
    }
    return initialPrefill?.html ?? followUpHandoff?.bodyHtml ?? "";
  })();

  const [renderedHtml, setRenderedHtml] = useState(initialBodyHtml);
  const [renderedText, setRenderedText] = useState(
    isReplyPrefill ? replyBody : (initialPrefill?.text ?? ""),
  );
  const [renderedSubject, setRenderedSubject] = useState("");

  const [previousBodyHtml, setPreviousBodyHtml] = useState<string | null>(null);

  // Inline banners driven by the Abstract API recipient validator on
  // `POST /send`. `recipientValidationError` is the structured 400
  // body for `code: "recipient_invalid"` — one row per blocked
  // address. `recipientWarnings` is the always-present `warnings`
  // array on the success response, used to show "we sent it but
  // these addresses look risky" advisories after a successful send.
  const [recipientValidationError, setRecipientValidationError] = useState<
    | {
        field: "to" | "cc" | "bcc";
        email: string;
        verdict: { status: "invalid"; reasons: string[] };
      }[]
    | null
  >(null);
  const [recipientWarnings, setRecipientWarnings] = useState<
    | {
        field: "to" | "cc" | "bcc";
        email: string;
        verdict: { status: "risky"; reasons: string[] };
      }[]
    | null
  >(null);

  const parsedTemplateId = parseInt(templateId, 10);
  const isTemplated =
    !!templateId && templateId !== "blank" && Number.isFinite(parsedTemplateId);
  const isBlank = templateId === "blank";

  const { data: selectedTemplate } = useGetTemplate(parsedTemplateId, {
    query: {
      enabled: isTemplated,
      queryKey: getGetTemplateQueryKey(parsedTemplateId),
    },
  });

  const renderMutation = useRenderTemplate();
  const sendMutation = useSendEmail();
  // Track which kind of send is in flight so the per-button spinner only
  // appears on the button the user actually pressed. `useSendEmail` is
  // shared across both the "Send Email" and "Send test" buttons (and in
  // theory could be called concurrently), so we tag the in-flight mode
  // ourselves rather than relying on `sendMutation.isPending` alone.
  const [sendInFlight, setSendInFlight] = useState<"live" | "test" | null>(null);
  const aiGenerateMutation = useAiGenerateTemplate();
  const createTemplateMutation = useCreateTemplate();
  // PATCH /api/me/preferences. Powers the inline "Save as default" button
  // next to the per-send From-name + alias inputs below — saves whatever
  // the user has typed into those overrides as their per-user defaults
  // without making them open the Brand Settings page first.
  const updateMePreferencesMutation = useUpdateMePreferences();

  // Default the template picker to the Post-Call Thank You template the
  // first time the templates list resolves. We only do this when the user
  // is starting a fresh new compose (no URL ?templateId=, no reply
  // pre-fill, no "Use as new email", no follow-up handoff) so we never
  // override an explicit user intent. We also stop after the first
  // successful auto-select so manually clearing/changing the picker is
  // sticky for the rest of the session.
  const didAutoSelectDefaultRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectDefaultRef.current) return;
    if (!isFreshNewCompose) {
      didAutoSelectDefaultRef.current = true;
      return;
    }
    if (templateId !== "") return;
    if (!templates || templates.length === 0) return;
    const def = templates.find((t) => t.slug === DEFAULT_TEMPLATE_SLUG);
    if (def) {
      didAutoSelectDefaultRef.current = true;
      setTemplateId(String(def.id));
    }
  }, [templates, isFreshNewCompose, templateId]);

  // Reset draft state whenever the user picks a different template.
  // Skip the very first run so a pre-fill (from inbox reply query params,
  // "Use as new email", or "Draft follow-up") survives mount.
  const skipFirstResetRef = useRef(
    isReplyPrefill || !!initialPrefill || !!followUpHandoff,
  );
  useEffect(() => {
    if (skipFirstResetRef.current) {
      skipFirstResetRef.current = false;
      return;
    }
    setSubjectMode("auto");
    setManualSubject("");
    setVariables({});
    setRenderedHtml("");
    setRenderedText("");
    setRenderedSubject("");
    setPreviousBodyHtml(null);
    setAutoFilledCaseRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // One-shot toast when we land here from "Use as new email".
  useEffect(() => {
    if (initialPrefill) {
      toast.success(
        "Loaded a copy of the previous email — edit anything, then send.",
      );
    } else if (followUpHandoff) {
      toast.success(
        "Loaded a follow-up draft — review the message, then send.",
      );
    }
    // initialPrefill / followUpHandoff are computed once on mount so this is a true mount-only effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill the From-name and Reply-To inputs once Brand Settings load.
  // Each field uses the user's saved brand value when present (so people
  // who have customized "Acme Support" / "help@acme.com" see their own
  // settings), falling back to the global "Support Team" /
  // "support@cloudaiassistance.com" defaults otherwise. We skip the prefill
  // entirely once the user has typed in a field so we never clobber edits,
  // and we skip Reply-To when an inbox-prefill ("Reply" from received) has
  // already seeded the original sender.
  useEffect(() => {
    // We must wait for BOTH brand-wide settings and `/me` (per-user
    // defaults) before seeding the From inputs — otherwise we'd race:
    // brand loads first, we'd seed with brand value, then `/me` arrives
    // with the user's saved `defaultFromName` but the field is no longer
    // empty, so the user's preference would be silently ignored.
    // `meFetched` is true once the /me query has settled (success or
    // error), so a logged-out / 401 path also unblocks prefill.
    if (!brandSettings || !meFetched) return;
    if (
      !replyToTouchedRef.current &&
      !isReplyPrefill &&
      replyToOverride === ""
    ) {
      // Default Reply-To rule: the local-part must mirror the user's
      // sent-from email username so each agent's replies are routed to
      // their own inbox by default (not a shared catch-all). The domain
      // comes from the brand-wide support address so the routing stays
      // on the workspace's reply domain (e.g. update2factor.com), even
      // when the From domain is the verified sending domain
      // (e.g. cloudaiassistance.com). Falls back, in order, to:
      //   1) connected Gmail address (replies go to the user's Gmail
      //      inbox they explicitly linked)
      //   2) Clerk /me email (signed-in identity)
      //   3) brand support email
      //   4) hardcoded default
      // so the field is never empty and existing connected-Gmail or
      // self-routing setups keep working.
      const fromLocal = (
        meUser?.defaultFromLocalPart ||
        brandSettings.fromLocalPart ||
        ""
      )
        .trim()
        .toLowerCase();
      const replyToFallback =
        brandSettings.supportEmail || DEFAULT_REPLY_TO;
      const replyDomain = replyToFallback.includes("@")
        ? replyToFallback.split("@")[1]
        : "";
      const usernameDefault =
        fromLocal && replyDomain ? `${fromLocal}@${replyDomain}` : "";
      setReplyToOverride(
        usernameDefault ||
          (gmailStatus?.connected ? gmailStatus.gmailAddress : null) ||
          meUser?.email ||
          replyToFallback,
      );
    }
    if (!fromNameTouchedRef.current) {
      // Per-user `defaultFromName` wins over the brand-wide setting so two
      // agents sharing one workspace can each sign emails with their own
      // name. Falls back to the brand setting and finally the hard-coded
      // DEFAULT_FROM_NAME so this field is never empty when the page loads.
      // We always re-apply (not gated on emptiness) because we're past the
      // load gate above and the touched-ref protects user edits.
      setFromNameOverride(
        meUser?.defaultFromName ||
          brandSettings.fromName ||
          DEFAULT_FROM_NAME,
      );
    }
    if (
      !fromLocalPartTouchedRef.current &&
      meUser?.defaultFromLocalPart
    ) {
      // Only seed the local-part field when the user has *explicitly*
      // saved a default — otherwise we leave it blank so the placeholder
      // shows the brand-wide alias (or the verified shared sender).
      // We do NOT seed from `brandSettings.fromLocalPart` here for the
      // same reason: a blank input + brand fallback was the prior UX
      // for users who don't care about per-send aliases.
      setFromLocalPartOverride(meUser.defaultFromLocalPart);
    }
    // We intentionally only react to brandSettings/meUser/gmailStatus
    // loading; the field values themselves change as a side-effect of
    // the setters above and re-running on those would create loops.
    // Touched-refs guard re-entry. `gmailStatus?.gmailAddress` is
    // included so a freshly-completed Gmail Connect updates the
    // Reply-To preview without forcing a page reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    brandSettings,
    meUser,
    meFetched,
    gmailStatus?.connected,
    gmailStatus?.gmailAddress,
  ]);

  useEffect(() => {
    if (selectedTemplate) {
      // Seed each template variable with: the rep default for matching keys
      // (repName / agentName / agentTitle / repPhone / employeeId / caseId)
      // first, then fall back to the template's own declared defaultValue.
      // Lets users save their rep info once and have it auto-filled into
      // every template that uses it.
      //
      // `agentName` is aliased to the saved Representative name so the
      // user's chosen rep identity flows into every built-in template
      // (Post-Call Thank You, PayPal Fraud Reassurance, support
      // follow-ups, etc., which all use `{{agentName}}`). We deliberately
      // do NOT fall back to the user's account first name — the rep name
      // is what the user explicitly chose to send under, and is often
      // intentionally different from the name on their sign-in account.
      // `agentTitle` is the agent's saved job title (e.g. "Senior Trust &
      // Safety Specialist") and drives `{{agentTitle}}` in templates.
      // `agentDepartment` is the agent's saved team / department line
      // (e.g. "Technical Support Team", "Trust & Safety Team"); built-in
      // signature blocks render it on the line beneath `{{agentTitle}}`.
      const repFallbacks: Record<string, string | null | undefined> = {
        repName: repDefaults?.repName,
        agentName: repDefaults?.repName,
        agentTitle: repDefaults?.agentTitle,
        agentDepartment: repDefaults?.agentDepartment,
        repPhone: repDefaults?.repPhone,
        // The saved Representative email auto-fills both the literal
        // `{{repEmail}}` placeholder and the `{{agentEmail}}` alias —
        // mirrors how `repName` doubles as `agentName` so seeded
        // templates can use either label without per-template wiring.
        repEmail: repDefaults?.repEmail,
        agentEmail: repDefaults?.repEmail,
        employeeId: repDefaults?.employeeId,
        caseId: repDefaults?.caseId,
      };
      // Per-mount auto-fill for the default Post-Call Thank You template:
      // today's date in `callDate`. This takes precedence over the template's
      // seeded defaultValue so a freshly-opened compose always shows today,
      // but only on the very first compose mount (`isFreshNewCompose`).
      // The matching `caseReference` value flows in via the auto-defaults
      // fetch below (rep's saved Case ID, incremented per fresh compose).
      const isDefaultTemplate =
        selectedTemplate.slug === DEFAULT_TEMPLATE_SLUG &&
        defaultTemplateAutoFillsRef.current !== null;
      const defaultFills: Record<string, string> = isDefaultTemplate
        ? {
            callDate: defaultTemplateAutoFillsRef.current!.callDate,
          }
        : {};
      const initialVars: Record<string, string> = {};
      // Resolve today once per effect run so every date-typed field shares
      // the same value even if the effect happens to straddle midnight.
      const today = todayDateString();
      selectedTemplate.variables.forEach((v) => {
        const isRepKey = Object.prototype.hasOwnProperty.call(
          repFallbacks,
          v.key,
        );
        const repVal = repFallbacks[v.key];
        const defaultFill = defaultFills[v.key];
        if (typeof defaultFill === "string" && defaultFill !== "") {
          initialVars[v.key] = defaultFill;
        } else if (typeof repVal === "string" && repVal !== "") {
          initialVars[v.key] = repVal;
        } else if (isRepKey) {
          // Rep-aliased keys (repName / agentName / repPhone /
          // employeeId / caseId) deliberately do NOT fall back to the
          // template's seed `defaultValue`. The seed values (e.g.
          // "Alex Rivera" for `agentName`) are sample placeholders, not
          // sensible defaults — without rep info saved they'd silently
          // sign every email as "Alex Rivera". Leave the field blank so
          // the placeholder prompts the user to fill it in (or save a
          // Representative profile).
          initialVars[v.key] = "";
        } else if (v.defaultValue) {
          initialVars[v.key] = v.defaultValue;
        } else if (v.type === "date") {
          // Auto-select today for any date-typed variable that isn't already
          // populated by a more-specific source (per-template default fill,
          // rep defaults, or the seed's own defaultValue). Mirrors the
          // `<input type="date">` `YYYY-MM-DD` value format.
          initialVars[v.key] = today;
        } else {
          initialVars[v.key] = "";
        }
      });
      setVariables(initialVars);

      // Fetch server-issued auto-defaults (sequence counters and the
      // rep-driven `caseReference` cursor) once per template selection. We
      // do this AFTER setVariables so the static defaults render immediately;
      // the auto-generated values flow in a tick later via a second
      // setVariables that only touches the keys the server returned. The
      // cancel flag protects against the user switching templates before
      // the request resolves.
      //
      // Trigger conditions:
      //   - any variable with its own `autoGenerate` descriptor (e.g. PayPal
      //     Fraud's dedicated `PYPL-FRD-` counter), OR
      //   - any `caseReference` variable without `autoGenerate` — the
      //     server resolves these from the rep's saved Case ID and
      //     increments on every fresh compose. Without this branch the
      //     post-call template (which previously relied on a client-side
      //     digit rotation in localStorage) would never see its
      //     case-reference value populated by the rep cursor.
      const hasAutoGen = selectedTemplate.variables.some(
        (v) => v.autoGenerate?.kind === "sequence",
      );
      const hasRepDrivenCaseRef = selectedTemplate.variables.some(
        (v) => v.key === "caseReference" && !v.autoGenerate,
      );
      const needsAutoDefaults = hasAutoGen || hasRepDrivenCaseRef;
      // Only issue once per template selection: if we've already fetched
      // server-side auto-defaults for THIS template id during this mount,
      // skip — otherwise an unrelated dep change (e.g. `repDefaults`
      // resolving after first paint) would trigger another sequence
      // increment and replace the user-visible value with a higher one.
      const alreadyIssued =
        autoDefaultsIssuedForTemplateIdRef.current === selectedTemplate.id;
      if (needsAutoDefaults && !alreadyIssued) {
        autoDefaultsIssuedForTemplateIdRef.current = selectedTemplate.id;
        let cancelled = false;
        getTemplateAutoDefaults(selectedTemplate.id)
          .then((res) => {
            if (cancelled) return;
            const overrides = res?.values ?? {};
            if (Object.keys(overrides).length === 0) return;
            setVariables((prev) => ({ ...prev, ...overrides }));
            if (typeof overrides.caseReference === "string" && overrides.caseReference) {
              setAutoFilledCaseRef(overrides.caseReference);
            }
            queryClient.invalidateQueries({ queryKey: getPeekNextCaseReferenceQueryKey() });
          })
          .catch(() => {
            // Non-fatal: the static defaultValue is already in the field,
            // so the user just sees e.g. the seed's `PYPL-FRD-170975201`
            // until they refresh. We don't toast — failing here would be
            // noisy and the form is still fully usable.
          });
        return () => {
          cancelled = true;
        };
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, repDefaults]);

  function handlePickContact(c: Contact) {
    if (c.email) setTo(c.email);
    // Pre-fill conventional customer-* template variables when present.
    if (selectedTemplate) {
      const customerFills: Record<string, string | null | undefined> = {
        customerName: c.name,
        customerEmail: c.email,
        customerPhone: c.phone,
        customerAddress: c.address,
      };
      setVariables((prev) => {
        const next = { ...prev };
        for (const v of selectedTemplate.variables) {
          const val = customerFills[v.key];
          if (val != null && val !== "") next[v.key] = val;
        }
        return next;
      });
    }
    setContactPickerOpen(false);
    setContactSearch("");
    toast.success(`Loaded contact: ${capitalizeName(c.name)}`);
  }

  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.email?.toLowerCase().includes(q) ?? false) ||
        (c.phone?.toLowerCase().includes(q) ?? false),
    );
  }, [contacts, contactSearch]);

  const debouncedVars = useDebounce(variables, 250);
  const debouncedManualSubject = useDebounce(manualSubject, 250);

  useEffect(() => {
    if (!isTemplated || !selectedTemplate) return;

    let isCancelled = false;
    const render = async () => {
      try {
        const res = await renderMutation.mutateAsync({
          id: parseInt(templateId, 10),
          data: {
            variables: debouncedVars,
            subjectOverride:
              subjectMode === "manual" ? debouncedManualSubject : undefined,
          },
        });
        if (!isCancelled) {
          setRenderedHtml(res.html);
          setRenderedText(res.text);
          setRenderedSubject(res.subject);
        }
      } catch {}
    };
    render();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTemplated,
    templateId,
    selectedTemplate,
    debouncedVars,
    subjectMode,
    debouncedManualSubject,
  ]);

  const effectiveSubject = isTemplated
    ? subjectMode === "manual"
      ? manualSubject
      : renderedSubject
    : blankSubject;

  // Verified shared sender — used for both fallback values and the
  // domain suffix shown next to the local-part input. The brand row's
  // fromDomain is the authoritative source; we slice the legacy fromEmail
  // as a fallback.
  const verifiedFromEmail = sender?.fromEmail ?? "";
  const verifiedDomain =
    brandSettings?.fromDomain ||
    (verifiedFromEmail.includes("@")
      ? verifiedFromEmail.split("@")[1]
      : "");
  const verifiedDefaultLocalPart = verifiedFromEmail.includes("@")
    ? verifiedFromEmail.split("@")[0]
    : "";

  // Effective From shown in the live preview line. Mirrors the server's
  // resolution order (per-send override → saved brand default → verified
  // shared sender) and the same regex sanity check, so what users see
  // matches exactly what the recipient will get.
  const effectiveFromName =
    fromNameOverride.trim() ||
    meUser?.defaultFromName ||
    brandSettings?.fromName ||
    "Support Team";
  const candidateLocal = (
    fromLocalPartOverride.trim() ||
    meUser?.defaultFromLocalPart ||
    brandSettings?.fromLocalPart ||
    verifiedDefaultLocalPart
  )
    .trim()
    .toLowerCase();
  const COMPOSE_LOCAL_PART_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;
  const effectiveFromLocal = COMPOSE_LOCAL_PART_RE.test(candidateLocal)
    ? candidateLocal
    : verifiedDefaultLocalPart;
  const fromDisplay = senderLoading
    ? "Loading sender…"
    : `${effectiveFromName} <${effectiveFromLocal}@${verifiedDomain}>`;
  const fromLocalPartInvalid =
    fromLocalPartOverride.trim().length > 0 &&
    !COMPOSE_LOCAL_PART_RE.test(fromLocalPartOverride.trim().toLowerCase());

  const toInvalid = useMemo(() => findInvalidRecipients(to), [to]);
  const ccInvalid = useMemo(() => findInvalidRecipients(cc), [cc]);
  const bccInvalid = useMemo(() => findInvalidRecipients(bcc), [bcc]);
  const toError = recipientErrorText("To", toInvalid);
  const ccError = recipientErrorText("Cc", ccInvalid);
  const bccError = recipientErrorText("Bcc", bccInvalid);
  const hasInvalidRecipients =
    toInvalid.length > 0 || ccInvalid.length > 0 || bccInvalid.length > 0;
  const invalidRecipientReason = toError ?? ccError ?? bccError;

  // Reply-To is a single address, not a comma-separated list, so we
  // validate it independently of the To/Cc/Bcc recipient regex pipeline.
  // Empty/whitespace-only is treated as "unset" — the server falls back
  // to the From address.
  const trimmedReplyToOverride = replyToOverride.trim();
  const replyToOverrideInvalid =
    trimmedReplyToOverride.length > 0 &&
    !EMAIL_REGEX.test(trimmedReplyToOverride);
  const replyToOverrideError = replyToOverrideInvalid
    ? `Reply-To is not a valid email address: "${trimmedReplyToOverride}"`
    : null;

  // Same regex check, applied to template variables declared as `type: "email"`
  // or `type: "url"`. A typo here doesn't fail the send — it bakes a malformed
  // value into the body — so we mirror the recipient-field treatment to catch
  // it before Send.
  const invalidVars = useMemo(() => {
    if (!selectedTemplate)
      return [] as { key: string; label: string; kind: "email" | "url" }[];
    const out: { key: string; label: string; kind: "email" | "url" }[] = [];
    for (const v of selectedTemplate.variables) {
      const val = (variables[v.key] ?? "").trim();
      if (val.length === 0) continue;
      if (v.type === "email" && !EMAIL_REGEX.test(val)) {
        out.push({ key: v.key, label: v.label, kind: "email" });
      } else if (v.type === "url" && !isValidUrl(val)) {
        out.push({ key: v.key, label: v.label, kind: "url" });
      }
    }
    return out;
  }, [selectedTemplate, variables]);

  const invalidEmailVars = useMemo(
    () => invalidVars.filter((v) => v.kind === "email"),
    [invalidVars],
  );
  const invalidUrlVars = useMemo(
    () => invalidVars.filter((v) => v.kind === "url"),
    [invalidVars],
  );

  const hasInvalidEmailVars = invalidEmailVars.length > 0;
  const hasInvalidUrlVars = invalidUrlVars.length > 0;
  const hasInvalidVars = hasInvalidEmailVars || hasInvalidUrlVars;

  const invalidEmailVarReason = hasInvalidEmailVars
    ? invalidEmailVars.length === 1
      ? `"${invalidEmailVars[0].label}" is not a valid email address`
      : `Some template variables are not valid email addresses: ${invalidEmailVars
          .map((v) => `"${v.label}"`)
          .join(", ")}`
    : null;
  const invalidUrlVarReason = hasInvalidUrlVars
    ? invalidUrlVars.length === 1
      ? `"${invalidUrlVars[0].label}" is not a valid URL`
      : `Some template variables are not valid URLs: ${invalidUrlVars
          .map((v) => `"${v.label}"`)
          .join(", ")}`
    : null;

  const hasInvalidInputs =
    hasInvalidRecipients || hasInvalidVars || replyToOverrideInvalid;
  const invalidInputsReason =
    invalidRecipientReason ??
    invalidEmailVarReason ??
    invalidUrlVarReason ??
    replyToOverrideError;

  const handleSend = async (opts: { testSend?: boolean } = {}) => {
    const isTest = opts.testSend === true;
    // For a test send the server overrides the recipient with the
    // signed-in user's own Clerk-managed email, so the typed To/Cc/Bcc
    // values are irrelevant. We skip the To-required check (and the
    // recipient-validation gate inside `hasInvalidInputs`) so a user can
    // preview a draft without first having to type a real recipient.
    if (!isTest && !to) {
      toast.error("Recipient (To) is required");
      return;
    }
    if (isTest) {
      // Keep the template-variable / reply-to validity gate even on a
      // test send (the user is still composing a real draft) — but skip
      // the recipient validity gate because the typed To/Cc/Bcc are
      // ignored by the server in test mode.
      const hasNonRecipientIssue =
        hasInvalidVars || replyToOverrideInvalid;
      if (hasNonRecipientIssue) {
        toast.error(
          invalidEmailVarReason ??
            invalidUrlVarReason ??
            replyToOverrideError ??
            "Fix invalid template variables first",
        );
        return;
      }
    } else if (hasInvalidInputs) {
      toast.error(invalidInputsReason ?? "Fix invalid template variables first");
      return;
    }
    if (!isTemplated && !isBlank) {
      toast.error("Please select a template or choose Blank Email");
      return;
    }
    if (isTest && !meUser?.email) {
      toast.error(
        "Could not find your sign-up email. Add a verified email to your account to use Send test.",
      );
      return;
    }

    try {
      const toArr = splitRecipients(to);
      const ccArr = splitRecipients(cc);
      const bccArr = splitRecipients(bcc);
      const baseData = {
        to: toArr.length === 1 ? toArr[0] : toArr,
        cc: ccArr.length > 0 ? (ccArr.length === 1 ? ccArr[0] : ccArr) : null,
        bcc: bccArr.length > 0 ? (bccArr.length === 1 ? bccArr[0] : bccArr) : null,
        subject: effectiveSubject,
      };

      const attachmentPayload =
        attachments.length > 0
          ? attachments.map((a) => ({
              filename: a.filename,
              content: a.content,
              contentType: a.contentType,
            }))
          : undefined;

      // Per-send From overrides — only sent when the user typed something
      // *different* from the resolved default that the server would already
      // apply on its own. Empty/whitespace strings, or values that exactly
      // match the brand → fallback chain, are treated identically to
      // "unset" so the server runs its own fallback (and any later brand
      // edits take effect even on a tab that was opened before the change).
      const trimmedNameOverride = fromNameOverride.trim();
      const trimmedLocalOverride = fromLocalPartOverride.trim().toLowerCase();
      const resolvedFromNameDefault =
        brandSettings?.fromName || DEFAULT_FROM_NAME;
      const resolvedReplyToDefault =
        brandSettings?.supportEmail || DEFAULT_REPLY_TO;
      const nameOverrideToSend =
        trimmedNameOverride && trimmedNameOverride !== resolvedFromNameDefault
          ? trimmedNameOverride
          : "";
      const replyToOverrideToSend =
        trimmedReplyToOverride &&
        trimmedReplyToOverride !== resolvedReplyToDefault
          ? trimmedReplyToOverride
          : "";
      const fromOverrides: Partial<SendEmailInput> = {
        ...(nameOverrideToSend
          ? { fromNameOverride: nameOverrideToSend }
          : {}),
        ...(trimmedLocalOverride
          ? { fromLocalPartOverride: trimmedLocalOverride }
          : {}),
        ...(replyToOverrideToSend
          ? { replyToOverride: replyToOverrideToSend }
          : {}),
      };

      // When this compose was opened as a reply from the inbox,
      // forward the inbound email id so the server can wire up the
      // RFC-2822 In-Reply-To/References headers and persist the
      // thread on the outgoing email log.
      const threadingPayload = replyInboundEmailId
        ? { inReplyToInboundEmailId: replyInboundEmailId }
        : undefined;

      // The "to" field is required by the schema, but on a test send the
      // server ignores its value and substitutes the user's own Clerk
      // email — so we still send the typed value (or a harmless
      // placeholder when empty) so the request validates, knowing the
      // server will overwrite it.
      const dataTo = isTest
        ? (to.trim() ? baseData.to : (meUser?.email ?? "test@example.com"))
        : baseData.to;
      const baseDataForSend = { ...baseData, to: dataTo };
      const testSendField: Partial<SendEmailInput> = isTest
        ? { testSend: true }
        : {};

      const data: SendEmailInput = isTemplated
        ? {
            ...baseDataForSend,
            ...fromOverrides,
            ...testSendField,
            templateId: parseInt(templateId, 10),
            variables,
            ...(subjectMode === "manual" ? { subject: manualSubject } : {}),
            ...(attachmentPayload ? { attachments: attachmentPayload } : {}),
            ...(threadingPayload ?? {}),
          }
        : {
            ...baseDataForSend,
            ...fromOverrides,
            ...testSendField,
            templateId: null,
            html: renderedHtml,
            text: renderedText || null,
            ...(attachmentPayload ? { attachments: attachmentPayload } : {}),
            ...(threadingPayload ?? {}),
          };

      // Clear any prior validation banner — we're attempting again.
      setRecipientValidationError(null);
      setRecipientWarnings(null);

      setSendInFlight(isTest ? "test" : "live");
      const sent = await sendMutation.mutateAsync({ data });
      // Risky-recipient advisories: queueing succeeded, but Abstract
      // flagged one or more addresses (role address, unknown
      // deliverability, etc.). Surface as a non-blocking toast +
      // inline banner so users notice without it feeling like an
      // error.
      const warnings = (sent.warnings ?? []).filter(
        (w): w is {
          field: "to" | "cc" | "bcc";
          email: string;
          verdict: { status: "risky"; reasons: string[] };
        } => w.verdict?.status === "risky",
      );
      // Rare path: the QStash worker ran inline (e.g. test envs) and
      // already flipped the row to `failed` before we got the 202.
      // Surface the same failure notification we'd normally show via
      // the polled watcher, and skip the misleading "queued" toast.
      const toDisplay = Array.isArray(dataTo) ? dataTo.join(", ") : dataTo;
      if (sent.status === "failed") {
        notifySendFailure({
          logId: sent.id,
          to: toDisplay,
          errorMessage: sent.errorMessage ?? null,
          providerErrorMessage: sent.providerErrorMessage ?? null,
        });
      } else if (warnings.length > 0) {
        setRecipientWarnings(warnings);
        toast.warning(
          warnings.length === 1
            ? `Queued — but ${warnings[0].email} looks risky`
            : `Queued — ${warnings.length} recipients flagged as risky`,
        );
      } else if (isTest) {
        toast.success(
          `Test email queued — sending to ${meUser?.email ?? "your inbox"}`,
        );
      } else {
        toast.success("Email queued — sending now");
      }

      // Kick off a background poll on the just-queued log row so that
      // if the worker later flips the row to `failed`, the user gets
      // an in-app toast (with the stored `errorMessage`) instead of a
      // silent flip in the Logs table. Skipped when the row is
      // already in a terminal state (e.g. inline-worker test envs).
      if (sent.status === "queued") {
        watchPendingSend({
          logId: sent.id,
          to: toDisplay,
          queryClient,
        });
      }

      queryClient.invalidateQueries({ queryKey: getListEmailLogsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getPeekNextCaseReferenceQueryKey() });

      // Send no longer mutates per-user or global rate-limit state, so
      // there is nothing on the dashboard summary to patch from the
      // post-send response. Other refresh triggers cover the rest.

      // On a normal send we redirect to /app/logs so the user sees the
      // queued row land in the log. On a test send we stay on Compose so
      // the user can keep iterating on the draft and fire another test
      // without losing context.
      if (!isTest) {
        setLocation(`/app/logs`);
      }
    } catch (e: unknown) {
      if (
        apiErrorStatus(e) === 400 &&
        apiErrorCode(e) === "recipient_invalid"
      ) {
        // Abstract API blocked one or more recipients. Surface a
        // toast + persistent inline banner so users can see *which*
        // address(es) failed and why; the banner stays until they
        // edit one of the recipient fields and re-send.
        const data = apiErrorData<{
          invalid?: {
            field: "to" | "cc" | "bcc";
            email: string;
            verdict: { status: "invalid"; reasons: string[] };
          }[];
        }>(e);
        const invalid = data?.invalid ?? [];
        if (invalid.length > 0) setRecipientValidationError(invalid);
        toast.error(
          invalid.length === 1
            ? `Recipient ${invalid[0].email} failed validation`
            : `${invalid.length} recipients failed validation`,
        );
      } else {
        const msg = apiErrorMessage(e, "Failed to send email");
        const looksActionable = /verified in Resend|MAILCRAFT_FROM_EMAIL/i.test(msg);
        toast.error(msg, looksActionable ? { duration: 12000 } : undefined);
      }
    } finally {
      setSendInFlight(null);
    }
  };

  if (senderLoading || templatesLoading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-64px)] md:h-screen overflow-hidden bg-background">
      <div className="h-16 px-6 border-b border-border flex items-center justify-between shrink-0 bg-card z-10 gap-4">
        <h1 className="font-bold text-lg flex items-center gap-2 shrink-0">
          <Mail className="w-5 h-5 text-primary" /> Compose
        </h1>
        <div className="flex items-center gap-3 min-w-0">
          <SenderStatusIndicator
            senderConfig={senderConfig}
            responsive
            showRecheckButton
            recheckButtonTestId="sender-recheck-button-unknown"
            fromName={effectiveFromName}
          />
          {/* Send test: routes the same payload through /send with
              testSend=true so the server overrides the recipient with
              the user's own Clerk-managed email and tags the log row
              with kind="test". */}
              {(() => {
                const hasSignupEmail = Boolean(meUser?.email);
                const hasNonRecipientIssue =
                  hasInvalidVars || replyToOverrideInvalid;
                const testDisabledReason = !meFetched
                  ? "Loading your account…"
                  : !hasSignupEmail
                    ? "Add a verified email to your account to use Send test."
                    : hasNonRecipientIssue
                      ? (invalidEmailVarReason ??
                        invalidUrlVarReason ??
                        replyToOverrideError ??
                        "Fix invalid template variables first")
                      : null;
                const testDisabled =
                  sendMutation.isPending ||
                  testDisabledReason !== null;
                const button = (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSend({ testSend: true })}
                    disabled={testDisabled}
                    aria-disabled={testDisabled || undefined}
                    data-testid="compose-send-test-button"
                    title={
                      hasSignupEmail && !testDisabledReason
                        ? `Send a preview to ${meUser?.email}`
                        : undefined
                    }
                  >
                    {sendInFlight === "test" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FlaskConical className="w-4 h-4 mr-2" />
                    )}
                    Send test
                  </Button>
                );
                if (testDisabledReason) {
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          data-testid="send-test-disabled-wrapper"
                        >
                          {button}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {testDisabledReason}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return button;
              })()}
          {hasInvalidInputs ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} data-testid="send-disabled-wrapper">
                  <Button
                    onClick={() => handleSend()}
                    disabled
                    aria-disabled="true"
                    data-testid="compose-send-button"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {invalidInputsReason}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              onClick={() => handleSend()}
              disabled={sendMutation.isPending}
              data-testid="compose-send-button"
            >
              {sendInFlight === "live" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          )}
        </div>
      </div>

      {isReplyPrefill && replyFromMailbox && (
        <div
          className="px-6 py-2 border-b border-primary/30 bg-primary/5 text-xs text-foreground shrink-0 flex items-center gap-2"
          data-testid="compose-reply-banner"
        >
          <Reply className="w-3.5 h-3.5 text-primary shrink-0" />
          <span>
            Replying to message received at{" "}
            <span className="font-mono">{replyFromMailbox}</span>. The actual
            From address remains your verified sender — change it any time
            in Brand Settings.
            {replyToParam && replyToParam === replyFromMailbox && (
              <>
                {" "}
                Reply-To is pre-filled with{" "}
                <span className="font-mono">{replyToParam}</span> so the
                recipient's reply lands back in this inbox — edit or clear
                it below if you want replies to go elsewhere.
              </>
            )}
          </span>
        </div>
      )}
      {isSenderUnverified && senderConfig && (
        <div
          className="px-6 py-2.5 border-b border-amber-500/30 bg-amber-500/10 flex items-start gap-3 text-xs text-amber-900 dark:text-amber-200 shrink-0"
          data-testid="sender-unverified-banner"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="flex-1">
            <span className="font-medium">
              Sender {senderConfig.address} isn't verified in Resend, so sends
              will be rejected.
            </span>{" "}
            {senderConfig.reason ?? null}
            {senderRecheckCooldown?.reason === "rate_limit" && (
              <span
                className="block mt-1 text-amber-800 dark:text-amber-300"
                role="status"
                aria-live="polite"
                data-testid="sender-recheck-cooldown-message"
              >
                Just a sec — Resend lets us recheck once every 10 seconds. Try
                again in {senderRecheckCooldown.secondsLeft} second
                {senderRecheckCooldown.secondsLeft === 1 ? "" : "s"}.
              </span>
            )}
          </span>
          <RecheckSenderButton
            className="shrink-0"
            testId="sender-recheck-button-unverified"
            onCooldownChange={setSenderRecheckCooldown}
          />
        </div>
      )}

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div className="w-full lg:w-1/2 flex flex-col border-r border-border bg-card overflow-auto">
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>From (this send only)</Label>
                  {/* Inline "Save as default" — turns the typed
                      per-send From name + alias into the user's saved
                      defaults so they pre-fill on every future
                      compose. Enabled only when the typed values
                      differ from what's already saved (and pass the
                      local-part validity gate); calls PATCH
                      /api/me/preferences. */}
                  {(() => {
                    const trimmedName = fromNameOverride.trim();
                    const trimmedLocal = fromLocalPartOverride
                      .trim()
                      .toLowerCase();
                    const savedName = meUser?.defaultFromName ?? "";
                    const savedLocal = meUser?.defaultFromLocalPart ?? "";
                    const differs =
                      trimmedName !== savedName ||
                      trimmedLocal !== savedLocal;
                    const localInvalid =
                      trimmedLocal.length > 0 && fromLocalPartInvalid;
                    const disabled =
                      !meFetched ||
                      !differs ||
                      localInvalid ||
                      updateMePreferencesMutation.isPending;
                    const tooltip = !meFetched
                      ? "Loading your account…"
                      : localInvalid
                        ? "Fix the From alias before saving."
                        : !differs
                          ? "These values already match your saved defaults."
                          : "Save these values as your defaults for every future compose.";
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                              onClick={async () => {
                                try {
                                  await updateMePreferencesMutation.mutateAsync({
                                    data: {
                                      defaultFromName: trimmedName,
                                      defaultFromLocalPart: trimmedLocal,
                                    },
                                  });
                                  toast.success(
                                    "Saved as your sending defaults",
                                  );
                                  queryClient.invalidateQueries({
                                    queryKey: getGetMeQueryKey(),
                                  });
                                } catch (e: unknown) {
                                  toast.error(
                                    apiErrorMessage(
                                      e,
                                      "Failed to save sending defaults",
                                    ),
                                  );
                                }
                              }}
                              disabled={disabled}
                              aria-disabled={disabled || undefined}
                              data-testid="compose-save-from-default-button"
                            >
                              {updateMePreferencesMutation.isPending ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : !differs &&
                                meFetched &&
                                (savedName || savedLocal) ? (
                                <Check className="w-3 h-3 mr-1" />
                              ) : (
                                <BookmarkPlus className="w-3 h-3 mr-1" />
                              )}
                              {!differs && meFetched && (savedName || savedLocal)
                                ? "Saved"
                                : "Save as default"}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {tooltip}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Input
                    value={fromNameOverride}
                    onChange={(e) => {
                      fromNameTouchedRef.current = true;
                      setFromNameOverride(e.target.value);
                    }}
                    placeholder={
                      meUser?.defaultFromName ||
                      brandSettings?.fromName ||
                      "Support Team"
                    }
                    maxLength={120}
                    className="h-9 text-xs"
                    data-testid="compose-from-name-input"
                    aria-label="From display name override"
                  />
                  <div className="flex items-stretch rounded-md border border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background overflow-hidden h-9">
                    <Input
                      value={fromLocalPartOverride}
                      onChange={(e) => {
                        fromLocalPartTouchedRef.current = true;
                        setFromLocalPartOverride(e.target.value.toLowerCase());
                      }}
                      placeholder={
                        meUser?.defaultFromLocalPart ||
                        brandSettings?.fromLocalPart ||
                        verifiedDefaultLocalPart
                      }
                      maxLength={64}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-xs h-full"
                      data-testid="compose-from-local-part-input"
                      aria-label="From local-part override"
                      aria-invalid={fromLocalPartInvalid || undefined}
                    />
                    <span
                      className="flex items-center px-2 bg-muted/40 text-[11px] text-muted-foreground font-mono border-l border-input"
                      data-testid="compose-from-domain-suffix"
                    >
                      @{verifiedDomain}
                    </span>
                  </div>
                </div>
                {fromLocalPartInvalid && (
                  <p
                    className="text-[11px] text-red-600 dark:text-red-400"
                    data-testid="compose-from-local-part-error"
                  >
                    Use 1-64 lowercase letters, digits, ".", "_" or "-".
                    Must start and end with a letter or digit.
                  </p>
                )}
                <div
                  className="rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px] font-mono truncate"
                  title={fromDisplay}
                  data-testid="compose-from-preview"
                >
                  {fromDisplay}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Leave blank to use your saved defaults from{" "}
                  <a
                    href="brand"
                    onClick={(e) => {
                      e.preventDefault();
                      setLocation("/app/brand");
                    }}
                    className="underline hover:text-foreground"
                  >
                    Brand Settings
                  </a>
                  , or use{" "}
                  <span className="font-medium text-foreground">
                    Save as default
                  </span>{" "}
                  above to make these values your defaults for every
                  future compose. The domain is fixed to your verified
                  shared sender.
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Template</Label>
                  <ComposeAiButton
                    isPending={
                      aiGenerateMutation.isPending ||
                      createTemplateMutation.isPending
                    }
                    onGenerated={async (input) => {
                      try {
                        const draft = await aiGenerateMutation.mutateAsync({
                          data: input,
                        });

                        // Patch the cached DashboardSummary so the AI usage
                        // banner (e.g. on template-new) reflects the new
                        // remaining/max/resetAt immediately rather than
                        // waiting for the next refresh.
                        const summaryKey = getGetDashboardSummaryQueryKey();
                        const cached =
                          queryClient.getQueryData<DashboardSummary>(summaryKey);
                        if (cached) {
                          queryClient.setQueryData<DashboardSummary>(summaryKey, {
                            ...cached,
                            aiRateLimitRemaining: draft.rateLimitRemaining,
                            aiRateLimitMax: draft.rateLimitMax,
                            aiRateLimitResetAt: draft.rateLimitResetAt,
                          });
                        } else {
                          queryClient.invalidateQueries({ queryKey: summaryKey });
                        }

                        const createInput: CreateTemplateInput = {
                          name: draft.name,
                          category: draft.category,
                          description: draft.description,
                          layout: draft.layout as CreateTemplateInputLayout,
                          subject: draft.subject,
                          previewText: draft.previewText ?? null,
                          bodyHtml: draft.bodyHtml,
                          variables: draft.variables,
                          sourcePrompt: draft.sourcePrompt ?? null,
                        };
                        const created =
                          await createTemplateMutation.mutateAsync({
                            data: createInput,
                          });
                        queryClient.invalidateQueries({
                          queryKey: getListTemplatesQueryKey(),
                        });
                        setTemplateId(created.id.toString());
                        toast.success(
                          `AI template "${created.name}" created and selected`,
                        );
                      } catch (e: unknown) {
                        if (apiErrorStatus(e) === 429) {
                          // AI hourly rate limit. Patch the cached
                          // DashboardSummary so the AI banner switches to
                          // the exhausted state without a reload.
                          const limit = apiErrorRateLimit(e);
                          if (limit) {
                            const summaryKey = getGetDashboardSummaryQueryKey();
                            const cached =
                              queryClient.getQueryData<DashboardSummary>(summaryKey);
                            if (cached) {
                              queryClient.setQueryData<DashboardSummary>(summaryKey, {
                                ...cached,
                                aiRateLimitRemaining: limit.remaining,
                                aiRateLimitMax: limit.max,
                                aiRateLimitResetAt: limit.resetAt,
                              });
                            } else {
                              queryClient.invalidateQueries({ queryKey: summaryKey });
                            }
                          }
                        }
                        toast.error(
                          apiErrorMessage(e, "AI generation failed"),
                        );
                      }
                    }}
                  />
                </div>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blank">Blank Email</SelectItem>
                    {templates?.map((t) => {
                      const slugAdjusted =
                        !t.isBuiltin && isAutoSuffixedSlug(t.name, t.slug);
                      return (
                        <SelectItem key={t.id} value={t.id.toString()}>
                          <span className="inline-flex items-center gap-1.5">
                            <span>{t.name}</span>
                            {slugAdjusted && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label="URL was adjusted to avoid a conflict"
                                    data-testid={`compose-slug-auto-suffix-indicator-${t.id}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }
                                    }}
                                    className="inline-flex items-center justify-center rounded text-blue-600 dark:text-blue-300 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                                  >
                                    <Info className="w-3.5 h-3.5" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-[260px] bg-popover text-popover-foreground border border-border"
                                >
                                  <p className="font-medium">
                                    URL was adjusted to avoid a conflict.
                                  </p>
                                  <p className="opacity-80 mt-1">
                                    This template's URL ends in{" "}
                                    <code className="font-mono text-[11px]">
                                      {t.slug}
                                    </code>{" "}
                                    because the name you chose is reserved or
                                    already in use.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              {recipientValidationError && (
                <div
                  className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-2"
                  data-testid="banner-recipient-invalid"
                >
                  <div className="font-medium text-destructive">
                    {recipientValidationError.length === 1
                      ? "Recipient failed validation"
                      : `${recipientValidationError.length} recipients failed validation`}
                  </div>
                  <ul className="space-y-1 text-xs">
                    {recipientValidationError.map((r) => (
                      <li
                        key={`${r.field}-${r.email}`}
                        className="flex items-start gap-2"
                      >
                        <span className="font-mono uppercase text-[10px] mt-0.5 text-destructive/80">
                          {r.field}
                        </span>
                        <span className="font-medium">{r.email}</span>
                        <span className="text-muted-foreground">
                          {r.verdict.reasons.length > 0
                            ? `— ${r.verdict.reasons.map(verdictReasonLabel).join(", ")}`
                            : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {recipientWarnings && recipientWarnings.length > 0 && (
                <div
                  className="rounded-md border border-amber-300/40 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm space-y-1"
                  data-testid="banner-recipient-warnings"
                >
                  <div className="font-medium text-amber-700 dark:text-amber-300">
                    Sent — but some recipients look risky
                  </div>
                  <ul className="space-y-0.5 text-xs">
                    {recipientWarnings.map((r) => (
                      <li key={`${r.field}-${r.email}`}>
                        <span className="font-mono uppercase text-[10px] text-amber-700/80 dark:text-amber-300/80 mr-1">
                          {r.field}
                        </span>
                        <span className="font-medium">{r.email}</span>
                        <span className="text-muted-foreground">
                          {r.verdict.reasons.length > 0
                            ? ` — ${r.verdict.reasons.map(verdictReasonLabel).join(", ")}`
                            : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <RecipientField
                label="To"
                value={to}
                onChange={setTo}
                placeholder="recipient@example.com"
                error={toError}
                testId="compose-to"
                trailing={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0"
                    onClick={() => setContactPickerOpen(true)}
                    title="Pick from contacts"
                    data-testid="button-pick-contact"
                  >
                    <Users className="w-3.5 h-3.5 mr-1.5" />
                    Contacts
                  </Button>
                }
              />
              <RecipientField
                label="Cc"
                value={cc}
                onChange={setCc}
                placeholder="optional"
                error={ccError}
                testId="compose-cc"
              />
              <RecipientField
                label="Bcc"
                value={bcc}
                onChange={setBcc}
                placeholder="optional"
                error={bccError}
                testId="compose-bcc"
              />
              <SingleAddressField
                label="Reply-To"
                value={replyToOverride}
                onChange={(next) => {
                  replyToTouchedRef.current = true;
                  setReplyToOverride(next);
                }}
                placeholder="optional — replies go here instead of the From address"
                error={replyToOverrideError}
                testId="compose-reply-to"
                helperText={
                  gmailStatus?.connected && gmailStatus.gmailAddress
                    ? `Replies will go to your Google address: ${gmailStatus.gmailAddress}. Edit to route them elsewhere.`
                    : meUser?.email
                      ? `Replies will go to your account address: ${meUser.email}. Edit to route them elsewhere.`
                      : "Leave blank to use the From address. Useful when you want replies routed to a shared inbox."
                }
              />
              <div className="flex items-start gap-2 pt-2">
                <Label className="w-12 text-muted-foreground text-right shrink-0 mt-2">
                  Files
                </Label>
                <div className="flex-1">
                  <AttachmentsField
                    attachments={attachments}
                    onChange={setAttachments}
                    buttonLabel="Attach files"
                    helperText="Drop a PDF or image (PNG, JPEG, GIF, WebP) here or click to attach. Up to 25 MB total — useful for wire receipts, screenshots, and similar."
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Label className="w-12 text-muted-foreground text-right shrink-0">
                  Subject
                </Label>
                {isTemplated ? (
                  subjectMode === "auto" ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={renderedSubject}
                        readOnly
                        className="h-9 font-medium bg-muted/40"
                        placeholder="Will render from template variables"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 shrink-0"
                        onClick={() => {
                          setManualSubject(renderedSubject);
                          setSubjectMode("manual");
                        }}
                        title="Override subject"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={manualSubject}
                        onChange={(e) => setManualSubject(e.target.value)}
                        className="h-9 font-medium"
                        placeholder="Custom subject"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 shrink-0"
                        onClick={() => {
                          setSubjectMode("auto");
                          setManualSubject("");
                        }}
                        title="Reset to template subject"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Reset
                      </Button>
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={blankSubject}
                      onChange={(e) => setBlankSubject(e.target.value)}
                      className="h-9 font-medium"
                      placeholder="Subject"
                    />
                    <SubjectsButton
                      bodyHtml={renderedHtml}
                      currentSubject={blankSubject}
                      variableKeys={[]}
                      onPick={(s) => setBlankSubject(s)}
                    />
                  </div>
                )}
                {isTemplated && subjectMode === "manual" && (
                  <SubjectsButton
                    bodyHtml={renderedHtml}
                    currentSubject={manualSubject}
                    variableKeys={
                      selectedTemplate?.variables.map((v) => v.key) ?? []
                    }
                    onPick={(s) => setManualSubject(s)}
                  />
                )}
              </div>
            </div>

            {isBlank && (
              <div className="pt-4 border-t border-border space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label>HTML Body</Label>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <ToneButton
                      bodyHtml={renderedHtml}
                      onApply={(newHtml) => {
                        setPreviousBodyHtml(renderedHtml);
                        setRenderedHtml(newHtml);
                      }}
                      onUndo={() => {
                        if (previousBodyHtml !== null) {
                          setRenderedHtml(previousBodyHtml);
                          setPreviousBodyHtml(null);
                          toast.success("Tone change undone");
                        }
                      }}
                      hasPrevious={previousBodyHtml !== null}
                    />
                    <SpamCheckButton
                      subject={blankSubject}
                      bodyHtml={renderedHtml}
                    />
                    <AiRateLimitInline />
                  </div>
                </div>
                <Textarea
                  value={renderedHtml}
                  onChange={(e) => {
                    setRenderedHtml(e.target.value);
                    setPreviousBodyHtml(null);
                  }}
                  rows={12}
                  className="font-mono text-sm"
                />
              </div>
            )}

            {isTemplated && (
              <div className="pt-2 flex items-center gap-2 flex-wrap">
                <SpamCheckButton
                  subject={effectiveSubject}
                  bodyHtml={renderedHtml}
                  disabled={!renderedHtml}
                />
                <AiRateLimitInline />
                <span className="text-[11px] text-muted-foreground">
                  Spam check runs on the rendered template + variables.
                </span>
              </div>
            )}

            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <div className="pt-6 border-t border-border">
                <h3 className="text-sm font-semibold mb-4">Variables</h3>
                <div className="space-y-4">
                  {selectedTemplate.variables.map((v) => {
                    const currentVal = variables[v.key] || "";
                    const trimmed = currentVal.trim();
                    const isInvalidEmail =
                      v.type === "email" &&
                      trimmed.length > 0 &&
                      !EMAIL_REGEX.test(trimmed);
                    const isInvalidUrl =
                      v.type === "url" &&
                      trimmed.length > 0 &&
                      !isValidUrl(trimmed);
                    const isInvalid = isInvalidEmail || isInvalidUrl;
                    const errorMessage = isInvalidEmail
                      ? `"${v.label}" is not a valid email address`
                      : isInvalidUrl
                        ? `"${v.label}" is not a valid URL — include http:// or https://`
                        : null;
                    const errorId = `var-${v.key}-error`;
                    // adminOnly variables: agents (non-admin) see the
                    // value but cannot edit it. We disable the input,
                    // dim it, and surface a tooltip on the lock icon
                    // explaining why. Admins see a normal editable
                    // field — same as any other variable.
                    const isLocked = !!v.adminOnly && !meUser?.isAdmin;
                    return (
                      <div key={v.key} className="space-y-1.5">
                        <Label className="flex justify-between">
                          <span className="flex items-center gap-1.5">
                            {v.label}{" "}
                            {v.required && (
                              <span className="text-destructive">*</span>
                            )}
                            {isLocked && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="inline-flex items-center text-muted-foreground"
                                    data-testid={`var-${v.key}-locked-icon`}
                                    aria-label="Locked — only an admin can change this"
                                  >
                                    <Info className="h-3 w-3" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  Only an admin can change this. Ask your admin if it needs updating.
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-mono">{`{{${v.key}}}`}</span>
                        </Label>
                        {v.type === "textarea" ? (
                          <Textarea
                            value={currentVal}
                            onChange={(e) =>
                              setVariables((prev) => ({
                                ...prev,
                                [v.key]: e.target.value,
                              }))
                            }
                            placeholder={v.placeholder || ""}
                            rows={3}
                            disabled={isLocked}
                            data-testid={`var-${v.key}-input`}
                          />
                        ) : (
                          <Input
                            type={
                              v.type === "email"
                                ? "email"
                                : v.type === "number"
                                  ? "number"
                                  : v.type === "url"
                                    ? "url"
                                    : v.type === "date"
                                      ? "date"
                                      : "text"
                            }
                            value={currentVal}
                            onChange={(e) =>
                              setVariables((prev) => ({
                                ...prev,
                                [v.key]: e.target.value,
                              }))
                            }
                            placeholder={v.placeholder || ""}
                            disabled={isLocked}
                            className={
                              isInvalid
                                ? "border-destructive ring-1 ring-destructive focus-visible:ring-destructive"
                                : undefined
                            }
                            aria-invalid={isInvalid ? "true" : "false"}
                            aria-describedby={isInvalid ? errorId : undefined}
                            data-testid={`var-${v.key}-input`}
                          />
                        )}
                        {isInvalid && errorMessage && (
                          <p
                            id={errorId}
                            className="text-xs text-destructive"
                            data-testid={`var-${v.key}-error`}
                          >
                            {errorMessage}
                          </p>
                        )}
                        {v.key === "caseReference" &&
                          autoFilledCaseRef &&
                          currentVal === autoFilledCaseRef && (
                            <p
                              className="text-xs text-muted-foreground"
                              data-testid="compose-next-case-reference"
                            >
                              Auto-filled from cursor:{" "}
                              <code
                                className="text-[11px] bg-muted px-1 py-0.5 rounded font-medium"
                                data-testid="compose-next-case-reference-value"
                              >
                                {autoFilledCaseRef}
                              </code>
                            </p>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex">
          {isTemplated ? (
            // Templated compose: full multi-client preview (Gmail Web /
            // Mobile, Apple Mail, Outlook Desktop / Web) with the
            // compatibility issues panel underneath.
            <MultiClientPreview
              templateId={parsedTemplateId}
              variables={debouncedVars}
              subjectOverride={
                subjectMode === "manual" ? debouncedManualSubject : undefined
              }
              displaySubject={effectiveSubject}
            />
          ) : (
            // Blank compose: no template id is available so the
            // multi-client endpoint can't be used. Keep the original
            // single-client iframe so the user still sees their draft.
            <div className="w-full bg-[#f4f4f5] flex flex-col relative overflow-hidden">
              <div className="p-3 border-b border-border/50 bg-white/50 flex justify-between items-center shrink-0">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  Live Preview
                </span>
              </div>
              <div className="flex-1 p-4 sm:p-8 overflow-auto flex items-start justify-center">
                <div className="bg-white shadow-xl rounded-md w-full max-w-[600px] overflow-hidden flex flex-col border border-border/10 h-full max-h-[800px] min-h-[400px]">
                  <iframe
                    srcDoc={rewriteLinksForPreview(renderedHtml)}
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    className="w-full flex-1 bg-white border-0"
                    title="Email Preview"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={contactPickerOpen} onOpenChange={setContactPickerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pick a contact</DialogTitle>
            <DialogDescription>
              Selecting fills the To field and matching template variables
              (customer name, email, phone, address).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Search by name, email, or phone"
                className="pl-9"
                data-testid="input-contact-search"
                autoFocus
              />
            </div>
            <div className="max-h-80 overflow-auto border border-border rounded-md divide-y divide-border">
              {!contacts ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Loading contacts...
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {contacts.length === 0
                    ? "No contacts yet. Add some on the Contacts page."
                    : "No contacts match that search."}
                </div>
              ) : (
                filteredContacts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handlePickContact(c)}
                    className="w-full text-left p-3 hover:bg-muted/60 transition-colors"
                    data-testid={`button-pick-contact-${c.id}`}
                  >
                    <div className="font-medium text-sm">
                      {capitalizeName(c.name)}
                    </div>
                    {c.email && (
                      <div className="text-xs text-muted-foreground">
                        {c.email}
                      </div>
                    )}
                    {c.phone && (
                      <div className="text-xs text-muted-foreground">
                        {c.phone}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setContactPickerOpen(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface RecipientFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  error: string | null;
  testId: string;
  trailing?: React.ReactNode;
}

function RecipientField({
  label,
  value,
  onChange,
  placeholder,
  error,
  testId,
  trailing,
}: RecipientFieldProps) {
  const errorId = `${testId}-error`;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label className="w-12 text-muted-foreground text-right shrink-0">
          {label}
        </Label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={
            error
              ? "h-9 border-destructive ring-1 ring-destructive focus-visible:ring-destructive"
              : "h-9"
          }
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? errorId : undefined}
          data-testid={`${testId}-input`}
        />
        {trailing}
      </div>
      {error && (
        <p
          id={errorId}
          className="pl-14 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface SingleAddressFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  error: string | null;
  testId: string;
  helperText?: string;
}

// Variant of RecipientField for single-address fields (e.g. Reply-To)
// where commas are not expected. Mirrors the same layout, red-ring,
// `aria-invalid`, and helper-text-on-error pattern so users get a
// consistent validation experience across the form, plus an optional
// always-visible helper line below for fields that benefit from a
// "what is this?" explainer.
function SingleAddressField({
  label,
  value,
  onChange,
  placeholder,
  error,
  testId,
  helperText,
}: SingleAddressFieldProps) {
  const errorId = `${testId}-error`;
  const helperId = `${testId}-helper`;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Label className="w-12 text-muted-foreground text-right shrink-0">
          {label}
        </Label>
        <Input
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={
            error
              ? "h-9 border-destructive ring-1 ring-destructive focus-visible:ring-destructive"
              : "h-9"
          }
          aria-invalid={error ? "true" : "false"}
          aria-describedby={
            error ? errorId : helperText ? helperId : undefined
          }
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          data-testid={`${testId}-input`}
        />
      </div>
      {error ? (
        <p
          id={errorId}
          className="pl-14 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      ) : helperText ? (
        <p
          id={helperId}
          className="pl-14 text-[11px] text-muted-foreground"
          data-testid={`${testId}-helper`}
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

interface ComposeAiButtonProps {
  isPending: boolean;
  onGenerated: (input: AiGenerateInput) => Promise<void>;
}

function ComposeAiButton({ isPending, onGenerated }: ComposeAiButtonProps) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [category, setCategory] = useState("");
  const [layoutHint, setLayoutHint] = useState<string>("");
  const [tone, setTone] = useState<string>("");

  const submit = async () => {
    if (prompt.trim().length < 4) {
      toast.error("Prompt must be at least 4 characters");
      return;
    }
    const input: AiGenerateInput = {
      prompt,
      category: category || null,
      layoutHint: (layoutHint || null) as AiGenerateInputLayoutHint | null,
      tone: (tone || null) as AiGenerateInputTone | null,
    };
    await onGenerated(input);
    setOpen(false);
    setPrompt("");
    setCategory("");
    setLayoutHint("");
    setTone("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={isPending}
        >
          {isPending ? (
            <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
          ) : (
            <Sparkles className="w-3 h-3 mr-1.5" />
          )}
          Generate with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Generate Email with AI
          </DialogTitle>
          <DialogDescription>
            Describe the email you need. We will generate a draft, save it as a
            template, and select it for sending. You can edit it before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>What do you need?</Label>
            <Textarea
              rows={4}
              placeholder="e.g. A friendly reminder to a customer about an unpaid invoice. Should include the invoice number, amount, and a payment link."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Category</Label>
              <Input
                placeholder="Optional"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Layout</Label>
              <Select value={layoutHint} onValueChange={setLayoutHint}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Auto" />
                </SelectTrigger>
                <SelectContent>
                  {AI_LAYOUT_HINT_VALUES.map((v) => (
                    <SelectItem key={v} value={v}>{LAYOUT_LABELS[v]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Auto" />
                </SelectTrigger>
                <SelectContent>
                  {AI_TONE_VALUES.map((v) => (
                    <SelectItem key={v} value={v}>{AI_TONE_LABELS[v]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
